import { useRef, useState } from 'react';

import { ArrowUpTrayIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

import { StatusBadge } from '@/features/decisions/components/backtests/StatusBadge';
import { useScenarios } from '@/features/decisions/hooks/useScenarios';
import { useSubmitExecution } from '@/features/decisions/hooks/useSubmitExecution';
import { useUserSession } from '@/hooks/useUserSession';

// The service caps limit at 200. The picker wants every scenario, not a page of them, and
// a catalog this size is one cheap read.
const PICKER_LIMIT = 200;

const CONTROL_CLASS =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-[14px] text-[#1A1C2E] focus:border-[#274483] focus:outline-none';

type ParsedConfig = { ok: true; value: Record<string, unknown> } | { ok: false; error: string };

function parseConfig(raw: string): ParsedConfig | null {
  if (!raw.trim()) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ok: false, error: 'Config must be a JSON object.' };
    }
    return { ok: true, value: value as Record<string, unknown> };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Not valid JSON.' };
  }
}

export function SubmitRunForm() {
  const { email } = useUserSession();
  const fileInput = useRef<HTMLInputElement>(null);
  // No polling: this is a picker, and creating a scenario already invalidates the query.
  const { data, isPending: scenariosPending } = useScenarios({ limit: PICKER_LIMIT, poll: false });
  const submitExecution = useSubmitExecution();

  const [scenarioRunId, setScenarioRunId] = useState('');
  const [rawConfig, setRawConfig] = useState('');
  // Regenerated after every accepted submission, so the next run is a new operation. Held
  // in state rather than computed per render, which is the whole point: a retry after a
  // timeout must reuse the same key or it starts a second simulation.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const scenarios = data?.scenarios ?? [];
  const parsed = parseConfig(rawConfig);
  const configScenario = parsed?.ok ? parsed.value.scenario_run_id : undefined;
  const mismatch =
    Boolean(scenarioRunId) && parsed?.ok === true && configScenario !== scenarioRunId
      ? `This config is for ${JSON.stringify(configScenario) || 'no scenario'}, but you picked "${scenarioRunId}". They must match — results are located in ClickHouse by this value.`
      : null;
  const canSubmit = Boolean(scenarioRunId) && parsed?.ok === true && !mismatch && !submitExecution.isPending;
  const result = submitExecution.data;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setRawConfig(await file.text());
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || !parsed?.ok) return;

    submitExecution.mutate(
      { scenarioRunId, configBody: parsed.value, submittedBy: email, idempotencyKey },
      {
        onSuccess: () => {
          setRawConfig('');
          if (fileInput.current) fileInput.current.value = '';
          setIdempotencyKey(crypto.randomUUID());
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="px-6 pt-5 pb-3">
        <h2 className="text-[16px] font-semibold text-[#1A1C2E]">Step 2 — Submit a run</h2>
        <p className="mt-1 text-[13px] text-[#5C5F72]">
          Sends the config to Execution Service and records the run. Submitting starts a real simulation and cannot be
          undone.
        </p>
      </div>

      <div className="grid gap-5 border-t border-gray-200 px-6 py-5">
        <label className="block max-w-md">
          <span className="mb-1.5 block text-[13px] font-semibold text-[#1A1C2E]">Scenario</span>
          <select
            value={scenarioRunId}
            onChange={(e) => setScenarioRunId(e.target.value)}
            disabled={scenariosPending}
            className={CONTROL_CLASS}
          >
            <option value="">{scenariosPending ? 'Loading scenarios…' : 'Select a scenario'}</option>
            {scenarios.map((s) => (
              <option key={s.scenario_run_id} value={s.scenario_run_id}>
                {s.label} ({s.scenario_run_id})
              </option>
            ))}
          </select>
          {!scenariosPending && scenarios.length === 0 && (
            <span className="mt-1.5 block text-[12px] text-[#9295A5]">No scenarios yet — create one in step 1.</span>
          )}
        </label>

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-4">
            <span className="text-[13px] font-semibold text-[#1A1C2E]">Config</span>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-[13px] font-medium text-[#274483] hover:text-[#4A6BAD]">
              <ArrowUpTrayIcon className="size-4" />
              Upload .json
              <input
                ref={fileInput}
                type="file"
                accept=".json,application/json"
                onChange={(e) => void handleFile(e.target.files?.[0])}
                className="hidden"
              />
            </label>
          </div>
          <textarea
            value={rawConfig}
            onChange={(e) => setRawConfig(e.target.value)}
            rows={10}
            spellCheck={false}
            placeholder={'{\n  "scenario_run_id": "…",\n  "actions": [ … ]\n}'}
            className={`${CONTROL_CLASS} font-mono text-[12px]`}
          />
          <span className="mt-1.5 block text-[12px] text-[#9295A5]">
            Paste the solution content, or upload the .json file. Its <code>scenario_run_id</code> must match the
            scenario above.
          </span>
          {parsed?.ok === false && <span className="mt-1 block text-[12px] text-[#B4291F]">{parsed.error}</span>}
          {mismatch && <span className="mt-1 block text-[12px] text-[#B4291F]">{mismatch}</span>}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-gray-200 px-6 py-4">
        <div className="min-h-5 text-[13px]">
          {submitExecution.isError && <span className="text-[#B4291F]">{submitExecution.error.message}</span>}
          {result && (
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[#1B7F53]">
              <CheckCircleIcon className="size-4 shrink-0" />
              Submitted
              <code className="text-[12px] text-[#5C5F72]">{result.execution.run_id}</code>
              <StatusBadge status={result.execution.status} />
            </span>
          )}
        </div>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex shrink-0 items-center justify-center rounded-md bg-[#1E2F52] px-4 py-2 text-[14px] font-semibold text-white enabled:hover:bg-[#274483] disabled:opacity-50"
        >
          {submitExecution.isPending ? 'Submitting…' : 'Submit run'}
        </button>
      </div>
    </form>
  );
}
